import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage, localizeQuestionHtml } from 'i18n';
import { motion } from 'motion/react';
import {
  SpeechBubble,
  EditQuestionIcon,
  EyeOpenIcon,
  EyeClosedIcon
} from 'assets/svgs';
import { Button, Dialog } from 'components';
import { Markup } from 'interweave';
import { FirebaseContext, LocalContext } from 'context';
import Question from '../Question';
import ToolbarDropdown from '../ToolbarDropdown';
import ScheduleLine from './ScheduleReveal/ScheduleLine';
import ScheduleRevealDialog from './ScheduleReveal/ScheduleRevealDialog';

function Questions({
  questions,
  pendingQuestionId,
  isCancelling,
  setSelectedQuestion,
  firebase,
  sessionId,
  sessionTimezone,
  handleClearSchedule,
  handleEditQuestion,
  handleScheduleQuestion,
  handleToggleQuestionVisibility,
  isModerator,
  uid,
  editingQuestion,
  editorState,
  setEditorState,
  isEditorReady,
  Editor,
  editorRef,
  editorIsFocused,
  setEditorIsFocused,
  editorStyle,
  toolbarStyle,
  getToolbarOptions,
  width,
  editorContent,
  handleCancelEdit,
  handleSaveEdit,
  uploadImageCallback,
  localization
}) {
  const t = useTranslations('Review');
  const tCommon = useTranslations('Common');
  const { language } = useLanguage();
  const [sessionComments, setSessionComments] = useState([]);
  const { user } = useContext(FirebaseContext);
  const { handleDialog } = useContext(LocalContext);
  const [questionPendingSchedule, setQuestionPendingSchedule] = useState(null);
  const [schedulingQuestion, setSchedulingQuestion] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const scheduleDialogRef = useRef(null);
  const canScheduleReveal =
    isModerator && Boolean(user?.email?.includes('agencyx.ie'));

  useEffect(() => {
    let unsubscribeToAllSessionComments;
    if (firebase && sessionId) {
      unsubscribeToAllSessionComments =
        firebase.questions.subscribeToAllSessionComments({
          sessionId,
          snapshot: (snapshot) => {
            const _sessionComments = [];
            if (!snapshot.empty) {
              snapshot.forEach((doc) => {
                _sessionComments.push({
                  id: doc.id,
                  ...doc.data()
                });
              });
            }
            setSessionComments(_sessionComments);
          }
        });
    }
    return () => {
      if (unsubscribeToAllSessionComments) {
        unsubscribeToAllSessionComments();
      }
    };
  }, [firebase, sessionId]);

  const handleDismissScheduleDialog = () => {
    if (schedulingQuestion) return;
    handleDialog({
      dialogRef: scheduleDialogRef,
      animation: 'dismiss',
      stateHandler: () => {
        setQuestionPendingSchedule(null);
        setScheduleError(null);
      }
    });
  };

  const handleConfirmSchedule = async ({
    scheduledVisibleAt,
    timezone,
    notifyOnReveal
  }) => {
    if (schedulingQuestion) return;
    try {
      setSchedulingQuestion(true);
      setScheduleError(null);
      await handleScheduleQuestion({
        questionId: questionPendingSchedule.id,
        scheduledVisibleAt,
        timezone,
        notifyOnReveal
      });
      handleDialog({
        dialogRef: scheduleDialogRef,
        animation: 'dismiss',
        stateHandler: () => setQuestionPendingSchedule(null)
      });
    } catch (error) {
      console.error('Error scheduling question reveal:', error);
      setScheduleError(t('scheduleSaveError'));
    } finally {
      setSchedulingQuestion(false);
    }
  };

  // The pending question was deleted or revealed by another moderator while
  // the dialog was open — nothing left to schedule.
  useEffect(() => {
    if (questionPendingSchedule && !schedulingQuestion) {
      const live = questions.find((q) => q.id === questionPendingSchedule.id);
      if (!live || live.isVisible) {
        handleDismissScheduleDialog();
      }
    }
  }, [questions, questionPendingSchedule, schedulingQuestion]);

  return (
    <Container layout transition={isCancelling ? undefined : { duration: 0 }}>
      {questions
        .filter((q) => q.questionId !== pendingQuestionId)
        .sort((a, b) => b.order - a.order)
        .map((question) => {
          const comments = sessionComments.filter(
            (_comment) => _comment.questionId === question.id
          );
          const myComments = comments.filter(
            (_comment) => _comment.uid === uid
          );
          return (
            <Question
              key={question.id}
              layout
              transition={isCancelling ? undefined : { duration: 0 }}
              question={question}
              editingQuestion={editingQuestion?.id === question.id}
              isHidden={!question.isVisible}
              setSelectedQuestion={setSelectedQuestion}
              setEditorIsFocused={setEditorIsFocused}
              markupContent={localizeQuestionHtml(question, language)}>
              {editingQuestion?.id === question.id ? (
                <>
                  {isEditorReady && Editor && (
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={setEditorState}
                      wrapperClassName="react-draft-wrapper"
                      toolbarClassName="react-draft-toolbar"
                      editorClassName="react-draft-editor"
                      ref={editorRef}
                      onFocus={() => setEditorIsFocused(true)}
                      onBlur={() => setEditorIsFocused(false)}
                      toolbar={getToolbarOptions({
                        uploadCallback: uploadImageCallback
                      })}
                      toolbarCustomButtons={
                        width < 550
                          ? [
                              <ToolbarDropdown
                                editorState={editorState}
                                onChange={setEditorState}
                              />
                            ]
                          : []
                      }
                      editorStyle={editorStyle({
                        editorIsFocused,
                        addingQuestion: false
                      })}
                      toolbarStyle={toolbarStyle}
                      placeholder={t('questionPlaceholder')}
                      localization={localization}
                      stripPastedStyles
                    />
                  )}
                  <EditQuestionButtonWrapper>
                    <Button
                      onClick={handleCancelEdit}
                      backgroundColor="var(--red)">
                      {tCommon('cancel')}
                    </Button>
                    <Button onClick={handleSaveEdit} disabled={!editorContent}>
                      {tCommon('save')}
                    </Button>
                  </EditQuestionButtonWrapper>
                </>
              ) : (
                <>
                  <Markup
                    content={localizeQuestionHtml(question, language)}
                    tagName="div"
                    className="markup"
                  />
                  {canScheduleReveal && !question.isVisible && (
                    <ScheduleLine
                      question={question}
                      onSchedule={() => setQuestionPendingSchedule(question)}
                      onClear={() =>
                        handleClearSchedule(question.id).catch((error) =>
                          console.error('Error clearing schedule:', error)
                        )
                      }
                    />
                  )}
                  <Comments
                    $myComments={myComments}
                    $isHidden={!question.isVisible}>
                    <div>
                      <SpeechBubble />
                      {t('commentCount', { count: comments.length })}
                    </div>
                    <div>
                      <SpeechBubble />
                      {t('myCommentCount', { count: myComments.length })}
                    </div>
                  </Comments>
                  <ButtonWrapper>
                    <CommentButtonWrapper $isHidden={!question.isVisible}>
                      <Button onClick={() => setSelectedQuestion(question)}>
                        {t('comment')}
                      </Button>
                    </CommentButtonWrapper>
                    {isModerator && (
                      <ModeratorControls>
                        {user?.email.includes('agencyx.ie') && (
                          <Button
                            $variant={width < 600 ? 'contained' : 'text'}
                            onClick={() =>
                              handleToggleQuestionVisibility(
                                question.questionId,
                                question.isVisible
                              )
                            }
                            icon={
                              question.isVisible ? (
                                <EyeOpenIcon
                                  width="1.25rem"
                                  style={{
                                    transform:
                                      width < 600
                                        ? 'scale(1.125)'
                                        : 'scale(1.25) translateX(1px)'
                                  }}
                                />
                              ) : (
                                <EyeClosedIcon
                                  width="1.25rem"
                                  style={{
                                    transform:
                                      width < 600
                                        ? 'scale(1.125)'
                                        : 'scale(1.25) translateX(1px)'
                                  }}
                                />
                              )
                            }
                            iconPosition="right">
                            {width > 600 && question.isVisible
                              ? 'Question Visible'
                              : width > 600 && !question.isVisible
                                ? 'Question Hidden'
                                : ''}
                          </Button>
                        )}

                        <Button
                          $variant={width < 600 ? 'contained' : 'text'}
                          onClick={() => handleEditQuestion(question)}
                          icon={
                            <EditQuestionIcon
                              width="1.25rem"
                              style={
                                width < 600
                                  ? {
                                      transform:
                                        'translateX(1px) translateY(-1px)'
                                    }
                                  : { transform: 'translateY(-1px)' }
                              }
                            />
                          }
                          iconPosition="right">
                          {width > 600 ? t('editQuestion') : ''}
                        </Button>
                      </ModeratorControls>
                    )}
                  </ButtonWrapper>
                </>
              )}
            </Question>
          );
        })}
      <Dialog
        ref={scheduleDialogRef}
        isVisible={!!questionPendingSchedule}
        onDismiss={handleDismissScheduleDialog}
        contentStyle={{ maxWidth: '454px' }}>
        <ScheduleRevealDialog
          question={questionPendingSchedule}
          sessionTimezone={sessionTimezone}
          onConfirm={handleConfirmSchedule}
          onDismiss={handleDismissScheduleDialog}
          saving={schedulingQuestion}
          error={scheduleError}
        />
      </Dialog>
    </Container>
  );
}

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Comments = styled.div`
  align-items: center;
  display: flex;
  /* Content-sized with a fixed gap — the old max-width + space-between box
     was tuned to the English string lengths (§9.5) */
  gap: 2rem;
  margin: 1.1rem 0 1rem;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.5 : 1)};
  width: fit-content;

  > div {
    align-items: center;
    color: var(--blue);
    display: flex;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 120%;

    svg {
      margin-right: 0.438rem;
      width: 1.063rem;
    }

    &:last-of-type {
      color: ${({ $myComments }) =>
        $myComments.length ? 'var(--blue)' : 'var(--red)'};

      svg {
        transform: rotate(180deg);

        path {
          fill: ${({ $myComments }) =>
            $myComments.length ? 'var(--blue)' : 'var(--red)'};
        }
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  button {
    .button-text {
      transform: translateY(1px);
    }
  }
`;

const CommentButtonWrapper = styled.div`
  opacity: ${({ $isHidden }) => ($isHidden ? 0.5 : 1)};
`;

const ModeratorControls = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;

  button {
    span {
      gap: 0.7em;
    }

    @media (max-width: 599px) {
      width: 40px;
      height: 40px;

      path {
        fill: #fff;
      }
    }

    @media (min-width: 600px) {
      &:first-of-type {
        left: 30px;
      }

      &:last-of-type {
        left: 15px;
      }
    }
  }
`;

const EditQuestionButtonWrapper = styled.div`
  bottom: 1.188rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  position: absolute;
  right: 1.188rem;
  z-index: 1;
`;

export default Questions;
